"use client";

import { useCallback, useState } from "react";
import { Copy } from "lucide-react";
import { DetailDrawer, DetailDrawerSkeleton } from "@/components/ui/detail-drawer";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { useManagerCoachingEmail } from "@/hooks/use-manager-coaching-email";
import { captureCoachingEmailCopied } from "@/lib/analytics";
import { copyToClipboard } from "@/lib/clipboard";

interface ManagerCoachingEmailDrawerProps {
  repEmail: string;
  accountId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManagerCoachingEmailDrawer({
  repEmail,
  accountId,
  open,
  onOpenChange,
}: ManagerCoachingEmailDrawerProps) {
  const [copied, setCopied] = useState(false);

  const { data, isLoading, isError } = useManagerCoachingEmail({
    repEmail,
    accountId,
    enabled: open,
  });

  const subject = data?.subject?.trim() ?? "";
  const body = data?.body?.trim() ?? "";
  const hasContent = Boolean(subject || body);

  const coachingLevel = accountId != null && accountId > 0 ? "account" : "rep";

  const copyBody = useCallback(async () => {
    if (!body) return;
    const didCopy = await copyToClipboard(body);
    if (!didCopy) return;
    captureCoachingEmailCopied({
      level: coachingLevel,
      rep_id: repEmail,
      ...(coachingLevel === "account" ? { account_id: accountId } : {}),
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  }, [body, coachingLevel, repEmail, accountId]);

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Draft Coaching Email"
      side="right"
      className="sm:max-w-2xl"
      showAiDisclaimer={false}
    >
      {isLoading ? <DetailDrawerSkeleton /> : null}

      {!isLoading && isError ? (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4">
          <Text as="p" size="sm" weight="semibold" color="default" className="text-destructive">
            Failed to generate coaching email draft.
          </Text>
          <Text as="p" size="sm" weight="normal" color="muted" className="mt-1">
            Please try again in a moment.
          </Text>
        </div>
      ) : null}

      {!isLoading && !isError && !hasContent ? (
        <div className="rounded-xl border border-border bg-card p-4">
          <Text as="p" size="sm" weight="semibold" color="default">
            No coaching email available
          </Text>
          <Text as="p" size="sm" weight="normal" color="muted" className="mt-1">
            We couldn&apos;t generate a draft for this coaching focus yet.
          </Text>
        </div>
      ) : null}

      {!isLoading && !isError && hasContent ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4">
            <div>
              <Text as="p" size="sm" weight="semibold" color="default" className="mb-2">
                Subject
              </Text>
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <Text as="p" size="sm" weight="normal" color="default">
                  {subject || "—"}
                </Text>
              </div>
            </div>

            <div>
              <Text as="p" size="sm" weight="semibold" color="default" className="mb-2">
                Body
              </Text>
              <div className="rounded-lg border border-border bg-card p-4">
                <pre className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                  {body || "—"}
                </pre>
              </div>
            </div>
          </div>

          <Button
            type="button"
            className="mt-6 w-full shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
            disabled={!body}
            onClick={copyBody}
          >
            <Copy className="mr-2 h-4 w-4" strokeWidth={2.25} />
            {copied ? "Copied" : "Copy to clipboard"}
          </Button>
        </div>
      ) : null}
    </DetailDrawer>
  );
}
